<template>
  <ButtonV2
    type="button"
    variant="subtle"
    size="lg"
    :disabled="isLoading"
    class="!font-sans"
    @click="handleGoogleOauth"
  >
    <svg
      v-if="!isLoading"
      class="w-5 h-5 mr-2"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
        fill="#4285F4"
      />
      <path
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
        fill="#34A853"
      />
      <path
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
        fill="#FBBC05"
      />
      <path
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
        fill="#EA4335"
      />
    </svg>
    <div
      v-else
      class="w-5 h-5 mr-2 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"
    ></div>
    {{
      isLoading
        ? t("channel.auth.oAuth.signingIn")
        : t("channel.auth.oAuth.continueWithGoogle")
    }}
  </ButtonV2>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useChannelStore } from "@/stores/channel.store";
import { useToastStore } from "@/stores/toast.store";
import { useOauthFlowManager } from "@/composables/auth/use-oauth-flow-manager";
import { AuthChannelType } from "@/generated/graphql";
import { useAuthentication } from "@/composables/auth/use-authentication";
import { useRouter } from "vue-router";
import { RouteNames } from "@/router/app.router";
import ButtonV2 from "@/components/ui/button/button-v2.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const props = defineProps<{
  isValidating: boolean;
}>();

const { authenticate } = useAuthentication();

const channelStore = useChannelStore();

const isInitiating = ref(false);
const router = useRouter();

const isLoading = computed(() => isInitiating.value || props.isValidating);

const toastStore = useToastStore();
const { startOAuthFlow } = useOauthFlowManager();

async function initiateGoogleOauthFlow(channelId: string) {
  const oauthCallbackUrl = new URL(
    router.resolve({
      name: RouteNames.OauthCallback,
      params: {
        provider: "google",
      },
    }).fullPath,
    window.location.origin,
  );
  oauthCallbackUrl.searchParams.set("client_id", channelId);

  const result = await authenticate({
    channelId,
    authChannel: AuthChannelType.Oauth,
    request: {
      channel_id: channelId,
      redirect_uri: oauthCallbackUrl.toString(),
    },
    options: {
      provider: "google",
    },
  });
  const data = result?.data?.authenticate;

  if (result?.error || !data || "error" in data) {
    console.error(
      "[WebThreeStrategy] Google OAuth initiation failed:",
      result?.error,
    );

    toastStore.addToast({
      message: result?.error?.toString() ?? "Unknown error",
      type: "error",
    });
    return null;
  }

  // The result should contain the Google OAuth URL
  if (data.code) {
    return data.code;
  }

  return null;
}

async function handleGoogleOauth() {
  if (!channelStore.channel?.id) {
    console.error("Channel not available");
    return;
  }

  isInitiating.value = true;

  try {
    // Get the Google OAuth URL
    const authUrl = await initiateGoogleOauthFlow(channelStore.channel.id);

    if (!authUrl) {
      console.error("Failed to get Google OAuth URL");
      toastStore.addToast({
        message: t("channel.auth.oAuth.failed"),
        type: "error",
      });
      return;
    }

    // Start OAuth flow using the manager
    await startOAuthFlow({
      authUrl,
      onError: (error: string) => {
        console.error("OAuth flow error:", error);
        toastStore.addToast({
          message: error,
          type: "error",
        });
        isInitiating.value = false;
      },
      onCancel: () => {
        console.log("OAuth flow cancelled by user");
        isInitiating.value = false;
      },
    });
  } catch (error) {
    console.error("Google OAuth error:", error);
    toastStore.addToast({
      message: t("channel.auth.oAuth.error"),
      type: "error",
    });
    isInitiating.value = false;
  } finally {
    isInitiating.value = false;
  }
}
</script>
